/**
 * This code was created with AI assistance to implement smooth, natural scrolling animations
 * with organic timing variations and proper end-of-scroll boundary handling.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';

import type { HeroFeedItem } from 'src/app/pages/psf/promoteOrShare/pageContext';
import { isMotionReduced } from 'src/app/shared/utils/accessibility';

import { checkScrollVisibility, findScrollTargets } from '../../utils';

export const smoothScrollTo = (element: HTMLElement, targetScrollTop: number, duration: number = 800) => {
	const startScrollTop = element.scrollTop;
	const maxScrollTop = element.scrollHeight - element.clientHeight;

	// Clamp the target to valid scroll bounds
	const clampedTarget = Math.max(0, Math.min(targetScrollTop, maxScrollTop));
	const distance = clampedTarget - startScrollTop;

	// If there's no distance to scroll, don't animate
	if (Math.abs(distance) < 1) {
		return;
	}

	const startTime = performance.now();
	const easeOutCubic = (t: number): number => 1 - (1 - t) ** 3;

	const animateScroll = (currentTime: number) => {
		const timeElapsed = currentTime - startTime;
		const progress = Math.min(timeElapsed / duration, 1);

		const easedProgress = easeOutCubic(progress);
		const newScrollTop = startScrollTop + distance * easedProgress;

		// Ensure we don't scroll beyond bounds during animation
		// oxlint-disable-next-line no-param-reassign
		element.scrollTop = Math.max(0, Math.min(newScrollTop, maxScrollTop));

		if (progress < 1) {
			requestAnimationFrame(animateScroll);
		}
	};

	requestAnimationFrame(animateScroll);
};

export const getRandomInterval = (baseSeconds: number) => {
	const shouldPause = Math.random() < 0.15; // 15% chance for longer pause
	const multiplier = shouldPause ? 1.8 : 1;
	const variation = baseSeconds * 0.2; // 20% variation
	const randomOffset = (Math.random() - 0.5) * 2 * variation;
	return (baseSeconds * multiplier + randomOffset) * 1000;
};

export const calculateScrollDistance = (scroller: HTMLElement) => {
	const children = Array.from(scroller.children);
	const firstChild = children[0];
	const secondChild = children[1];

	// Calculate actual distance dynamically by measuring the space between elements
	// This accounts for browser-specific rendering differences (Chrome vs Safari)
	if (firstChild instanceof HTMLElement && secondChild instanceof HTMLElement) {
		// The distance from the top of first element to the top of second element
		// This includes the element height + actual gap (no hardcoding)
		const actualDistance = secondChild.offsetTop - firstChild.offsetTop;
		return actualDistance;
	}

	// Fallback if we can't measure (shouldn't happen in practice)
	const itemHeight = firstChild instanceof HTMLElement ? firstChild.offsetHeight : 0;
	return itemHeight + 8; // fallback gap
};

export const isAtScrollEnd = (scroller: HTMLElement, last: HTMLElement | null) => {
	const maxScrollTop = scroller.scrollHeight - scroller.clientHeight;
	const isAtBottom = scroller.scrollTop >= maxScrollTop - 1; // Allow 1px tolerance
	const lastElementFullyVisible = last && checkScrollVisibility(scroller, last).bottom;
	return lastElementFullyVisible || isAtBottom;
};

export const useScrollAnimation = (
	scrollerRef: React.RefObject<HTMLDivElement | null>,
	intervalSeconds: number,
	shouldAnimate: boolean,
	setShouldAnimate: (animate: boolean) => void,
) => {
	useEffect(() => {
		const scroller = scrollerRef.current;
		if (!(scroller && shouldAnimate)) {
			return undefined;
		}

		let timeoutId: number | undefined;

		const handleInterval = () => {
			const { next, last } = findScrollTargets(scroller);

			if (isAtScrollEnd(scroller, last)) {
				setShouldAnimate(false);
				return;
			}

			if (!next) return;

			const scrollDistance = calculateScrollDistance(scroller);
			const targetScrollTop = scroller.scrollTop + scrollDistance;
			const maxScrollTop = scroller.scrollHeight - scroller.clientHeight;
			const clampedTargetScrollTop = Math.min(targetScrollTop, maxScrollTop);

			// If the clamped target is the same as current position, we're done
			if (Math.abs(clampedTargetScrollTop - scroller.scrollTop) < 1) {
				setShouldAnimate(false);
				return;
			}

			const animationDuration = 500 + Math.random() * 300; // 500-800ms
			smoothScrollTo(scroller, clampedTargetScrollTop, animationDuration);

			// Schedule next iteration with random timing
			const nextDelay = getRandomInterval(intervalSeconds);
			timeoutId = window.setTimeout(handleInterval, nextDelay);
		};

		// Start the first iteration with random timing
		const initialDelay = getRandomInterval(intervalSeconds);
		timeoutId = window.setTimeout(handleInterval, initialDelay);

		return () => {
			if (timeoutId !== undefined) {
				window.clearTimeout(timeoutId);
			}
		};
	}, [shouldAnimate, intervalSeconds, scrollerRef, setShouldAnimate]);
};

const ANIMATE_DELAY = 2000;
const ANIMATE_SPEED = 2000;
const PILL_DISPLAYED_COUNT_MAX = 4;

type HeroFeedResult = {
	// Mobile mode returns
	scrollerRef: React.RefObject<HTMLDivElement | null> | null;
	reversedItems: readonly HeroFeedItem[];
	// Desktop mode returns
	data: {
		displayedItems: readonly HeroFeedItem[];
		animateSpeed: number;
		animateDelay: number;
		a11y: {
			label: string;
			buttonLabel: string;
			isFeedAccessible: boolean;
		};
	};
	actions: {
		toggleFeedAccessibility: () => void;
	};
};

export function useHeroFeed(
	items: readonly HeroFeedItem[],
	intervalSeconds: number,
	mode: 'mobile' | 'desktop' = 'mobile',
): HeroFeedResult {
	const { translate } = useI18n();
	const isMobile = mode === 'mobile';
	const reversedItems = useMemo(() => [...items].reverse(), [items]);
	const [isFeedAccessible, setIsFeedAccessible] = useState(false);

	// Mobile vertical scroll logic
	const [shouldAnimate, setShouldAnimate] = useState(() => !isMotionReduced());
	const scrollerRef = useRef<HTMLDivElement>(null);
	useScrollAnimation(scrollerRef, intervalSeconds, isMobile && shouldAnimate, setShouldAnimate);

	// Desktop horizontal scroll logic
	const [displayItems, setDisplayItems] = useState(reversedItems);

	useEffect(() => {
		if (isMobile || displayItems.length <= 1) return undefined;

		const timeout = window.setTimeout(() => {
			setDisplayItems(displayItems.slice(1));
		}, ANIMATE_DELAY + ANIMATE_SPEED);

		return () => {
			window.clearTimeout(timeout);
		};
	}, [isMobile, displayItems]);

	const itemsTruncated = useMemo(
		() => (isMobile ? [] : displayItems.slice(0, PILL_DISPLAYED_COUNT_MAX)),
		[isMobile, displayItems],
	);

	return {
		scrollerRef: isMobile ? scrollerRef : null,
		reversedItems,
		data: {
			displayedItems: itemsTruncated,
			animateSpeed: ANIMATE_SPEED,
			animateDelay: ANIMATE_DELAY,
			a11y: {
				label: translate('corgi.psf.promote_or_share.supporters.summary'),
				buttonLabel: translate(`corgi.psf.promote_or_share.supporters.${isFeedAccessible ? 'hide' : 'view'}_all`),
				isFeedAccessible,
			},
		},
		actions: {
			toggleFeedAccessibility: () => setIsFeedAccessible((wasAccessible) => !wasAccessible),
		},
	};
}
